"use client";

import { motion, useReducedMotion } from "framer-motion";
import { useCallback, useEffect, useRef, useState, type AnimationEvent } from "react";
import { RollWidget } from "./roll-widget";
import type { RollWidgetAnchor } from "./roll-widget-slots";

/**
 * Les cœurs du récital : un bouton qu'on martèle, et une pluie qui monte.
 *
 * # Le geste
 *
 * Un cœur par pression, et rien à écrire : c'est la réaction qu'on fait sans
 * quitter le rouleau des yeux, à la manière des directs d'Instagram ou de
 * TikTok — le seul idiome que tout le monde connaisse déjà. Le sien monte tout
 * de suite, sans attendre le service ; ceux des autres arrivent par comptes
 * (`LiveHall.onHearts`), déjà nettoyés des siens, et tombent au même endroit.
 *
 * # Pourquoi les cœurs sont roses
 *
 * Le rouleau juste derrière peint le violet et le cyan des deux mains, l'ambre
 * des autres pistes, le gris des partenaires. Un cœur ne doit ressembler à
 * aucune note : rose et rouge sont les seules teintes que le rouleau ne prend
 * jamais, et ce sont celles d'un cœur partout ailleurs.
 *
 * # Ce que ça coûte au rouleau
 *
 * Des éléments DOM animés par `transform` et `opacity` seulement, composés
 * hors du fil principal ; jamais plus de {@link MAX_HEARTS} à la fois, et pas
 * plus de {@link MAX_PER_BURST} par compte reçu — quand la salle s'emballe, on
 * voit une pluie, pas un décompte. Le canvas du rouleau n'en sait rien.
 *
 * # Sans mouvement
 *
 * `prefers-reduced-motion` : les cœurs paraissent et s'effacent sur place,
 * sans monter (voir `globals.css`), et le compte fait le reste.
 */

/** D'où viennent les cœurs des autres : un abonnement, rendu avec le total de la salle. */
export type HeartsSource = (listener: (hearts: number, total: number) => void) => () => void;

type ReactionWidgetProps = {
  /**
   * Un cœur de plus, tapé ici. Absent, pas de bouton : on regarde tomber les
   * cœurs, on n'en envoie pas — c'est l'écran de l'interprète.
   */
  readonly onTap?: () => void;
  /** Les cœurs des autres, à faire tomber. */
  readonly hearts: HeartsSource;
  readonly anchor?: RollWidgetAnchor;
  /** Rang dans la pile de l'ancre, quand un autre widget y vit. */
  readonly order?: number;
};

const BUTTON_SIZE_PX = 48;
const RAIN_WIDTH_PX = 132;
const MAX_HEARTS = 40;
const MAX_PER_BURST = 12;
/** Un compte reçu s'étale sur la fenêtre où le service l'a compté (ms). */
const BURST_SPREAD_MS = 250;

const COLORS = ["#fb7185", "#f43f5e", "#fda4af", "#f472b6", "#ec4899"] as const;

const HEART_PATH =
  "M12 21s-6.7-4.4-9.3-8.6C.9 9.4 2.4 5.5 6 4.6c2-.5 4 .3 6 2.4 2-2.1 4-2.9 6-2.4 3.6.9 5.1 4.8 3.3 7.8C18.7 16.6 12 21 12 21z";

interface Heart {
  readonly id: number;
  /** Position de départ, en pour cent de la largeur de la colonne. */
  readonly x: number;
  readonly size: number;
  readonly durationMs: number;
  readonly delayMs: number;
  readonly swayPx: number;
  readonly swayMs: number;
  readonly color: string;
}

export function ReactionWidget({ onTap, hearts, anchor = "bottom-right", order }: ReactionWidgetProps) {
  const reducedMotion = useReducedMotion();
  const [rain, setRain] = useState<readonly Heart[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const nextId = useRef(0);
  const columnRef = useRef<HTMLDivElement | null>(null);

  // La hauteur de la colonne, posée en `--rise` pour les images-clés : une
  // unité de conteneur ne s'y résout pas (voir `globals.css`). Mesurée par un
  // observateur plutôt qu'à chaque cœur — la colonne ne change qu'avec le
  // rouleau, et une lecture de géométrie par cœur en pleine pluie serait une
  // mise en page forcée à chaque paquet.
  useEffect(() => {
    const column = columnRef.current;

    if (column === null || typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const height = entries[0]?.contentRect.height ?? 0;
      column.style.setProperty("--rise", `${Math.round(height)}px`);
    });

    observer.observe(column);

    return () => observer.disconnect();
  }, []);

  const spawn = useCallback((count: number, mine: boolean) => {
    if (count <= 0) {
      return;
    }

    const shown = Math.min(count, MAX_PER_BURST);

    setRain((current) => {
      const room = Math.max(0, MAX_HEARTS - current.length);
      const added: Heart[] = [];

      for (let i = 0; i < Math.min(shown, room); i += 1) {
        nextId.current += 1;

        added.push({
          id: nextId.current,
          x: mine ? 50 + (Math.random() - 0.5) * 24 : 12 + Math.random() * 76,
          size: mine ? 30 : 16 + Math.random() * 12,
          durationMs: 1_500 + Math.random() * 800,
          // Le sien part tout de suite ; un compte reçu s'étale sur sa fenêtre,
          // sans quoi douze cœurs partiraient collés en une seule grappe.
          delayMs: mine ? 0 : (i * BURST_SPREAD_MS) / shown,
          swayPx: 6 + Math.random() * 16,
          swayMs: 900 + Math.random() * 700,
          color: mine ? COLORS[1] : COLORS[Math.floor(Math.random() * COLORS.length)],
        });
      }

      return added.length === 0 ? current : [...current, ...added];
    });
  }, []);

  useEffect(() => hearts((count, published) => {
    setTotal((held) => Math.max(held ?? 0, published));
    spawn(count, false);
  }), [hearts, spawn]);

  const tap = () => {
    if (onTap === undefined) {
      return;
    }

    onTap();
    setTotal((held) => (held ?? 0) + 1);
    spawn(1, true);
  };

  const fell = (event: AnimationEvent<HTMLElement>, id: number) => {
    if (event.animationName !== "heart-rise") {
      return;
    }

    setRain((current) => current.filter((heart) => heart.id !== id));
  };

  return (
    <RollWidget anchor={anchor} order={order}>
      <div className="relative flex flex-col items-end gap-1.5">
        {/* La colonne où les cœurs montent : hors du flux, adossée au coin,
            et conteneur de taille pour que la montée se mesure sur elle. */}
        <div
          ref={columnRef}
          aria-hidden
          className="pointer-events-none absolute bottom-0 right-0"
          style={{
            width: RAIN_WIDTH_PX,
            // `cqh` : la hauteur du rouleau, que le calque des widgets donne.
            // Un peu plus de la moitié : chez l'interprète, les cœurs
            // s'effacent avant d'atteindre le widget du coin d'en haut.
            height: "min(56cqh, 320px)",
            overflow: "visible",
          }}
        >
          {rain.map((heart) => (
            <span
              key={heart.id}
              onAnimationEnd={(event) => fell(event, heart.id)}
              className="absolute bottom-0 block"
              style={{
                left: `${heart.x}%`,
                width: heart.size,
                height: heart.size,
                marginLeft: -heart.size / 2,
                animation: `heart-rise ${heart.durationMs}ms cubic-bezier(0.2, 0.7, 0.4, 1) ${heart.delayMs}ms both`,
                willChange: "transform, opacity",
              }}
            >
              <span
                className="block h-full w-full"
                style={{
                  ["--sway" as string]: `${heart.swayPx}px`,
                  animation: `heart-sway ${heart.swayMs}ms ease-in-out ${heart.delayMs}ms infinite`,
                }}
              >
                <svg viewBox="0 0 24 24" width="100%" height="100%" aria-hidden>
                  <path d={HEART_PATH} fill={heart.color} />
                </svg>
              </span>
            </span>
          ))}
        </div>

        {/* Le compte suit le bouton : sans bouton, c'est l'en-tête de « La
            salle » qui le porte, et deux fois le même nombre à dix pixels
            l'un de l'autre n'en disent pas plus. */}
        {onTap !== undefined && total !== null && total > 0 && (
          <span
            className="rounded-full px-2 py-0.5 font-mono text-[11px] tabular-nums"
            style={{
              color: "var(--color-text-secondary)",
              background: "var(--color-glass-shell)",
              border: "1px solid var(--color-border-subtle)",
            }}
            aria-live="off"
          >
            ♥&nbsp;{total}
          </span>
        )}

        {onTap !== undefined && (
          <motion.button
            type="button"
            onClick={tap}
            whileTap={reducedMotion ? undefined : { scale: 0.82 }}
            transition={{ type: "spring", stiffness: 700, damping: 22 }}
            aria-label="Envoyer un cœur"
            title="Envoyer un cœur"
            className="grid touch-manipulation select-none place-items-center rounded-full focus:outline-none focus-visible:ring-2"
            style={{
              width: BUTTON_SIZE_PX,
              height: BUTTON_SIZE_PX,
              color: COLORS[1],
              background: "var(--color-glass-shell)",
              backdropFilter: "blur(var(--blur-shell)) saturate(140%)",
              WebkitBackdropFilter: "blur(var(--blur-shell)) saturate(140%)",
              border: "1px solid var(--color-border-subtle)",
              boxShadow: "var(--shadow-popin-focus)",
              ["--tw-ring-color" as string]: "var(--color-accent)",
            }}
          >
            <svg aria-hidden width="22" height="22" viewBox="0 0 24 24">
              <path d={HEART_PATH} fill="currentColor" />
            </svg>
          </motion.button>
        )}
      </div>
    </RollWidget>
  );
}
